// ── EVERY FILE A TECHNICIAN HAS SENT IN ──────────────────────────────────
//
// They upload paperwork in three different places and it lands in three
// different shapes:
//
//   1. A CREDENTIAL — insurance, a licence, a W-9 — added from My Documents
//      or by Julian. It carries an expiry date and decides whether the man
//      can be paid.
//   2. The four NAMED uploads on the application form: W-9, insurance,
//      workers comp, contractor licence. Each sits in its own field with the
//      number that goes with it.
//   3. Anything else they attached, in `profile.documents`.
//
// The admin panel showed the first and the third. The four from the
// application were uploaded, stored, and never drawn on any screen — so a
// technician who attached his certificate of insurance when he signed up had
// sent Julian a file Julian could not open.

const name = (url) => {
  const last = String(url || '').split('?')[0].split('/').pop() || '';
  const clean = decodeURIComponent(last).replace(/\.[a-z0-9]{2,5}$/i, '').trim();
  // A Cloudinary id is a long unbroken run of letters and digits and tells
  // nobody anything. Dashes, underscores and spaces mean a person typed it:
  // "dopl-licence_2026" is a name and must survive.
  if (!clean || /^[a-z0-9]{16,}$/i.test(clean) || /^\d+$/.test(clean)) return '';
  return clean.replace(/[_-]+/g, ' ');
};

// The four named uploads on the application, in the order the form asks for
// them. Each carries the number recorded beside it, because a certificate
// and its policy number are not much use apart.
const APPLICATION = [
  { key: 'w9Url', title: 'W-9', num: (p) => (p.einNumber ? `EIN •••••${String(p.einNumber).slice(-4)}` : '') },
  { key: 'insuranceUrl', title: 'Liability Insurance', num: (p) => (p.insurancePolicyNumber ? `#${p.insurancePolicyNumber}` : '') },
  {
    key: 'workersCompUrl',
    // A waiver is not a lesser policy, it is a different document, and
    // calling both "Workers Comp" hides which one is on file.
    title: (p) => (p.workersCompType === 'waiver' ? 'Workers Comp Waiver' : 'Workers Comp'),
    num: () => '',
  },
  {
    key: 'contractorLicenseUrl',
    title: 'Contractor Licence',
    num: (p) => [p.qualifierLicenseNumber || p.licenseNumber, p.qualifierName]
      .filter(Boolean).join(' · '),
  },
];

/**
 * Every uploaded file on a vendor record, newest source first.
 *
 * @param {object} vendor
 * @param {(c: object) => string} label  what to call a credential
 * @param {(d: Date|string) => string} day  how to write a date
 * @returns {Array<{url, title, sub, note}>}
 */
export function vendorDocuments(vendor, label = (c) => c.label || c.type, day = (d) => String(d)) {
  const p = vendor?.profile || {};
  const out = [];
  const seen = new Set();
  const add = (d) => {
    const key = String(d.url || '').trim();
    // The same file can be attached twice — uploaded on the application and
    // then added again as a credential. One tile, and the credential wins,
    // because it is the copy with the expiry date on it.
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(d);
  };

  (vendor?.credentials || []).filter(c => c.documentUrl).forEach(c => add({
    url: c.documentUrl,
    title: label(c),
    sub: [c.issuer, c.number && `#${c.number}`].filter(Boolean).join(' · '),
    note: c.expiresAt ? `Expires ${day(c.expiresAt)}` : 'No expiry',
  }));

  APPLICATION.forEach((f) => {
    const url = p[f.key];
    if (!url) return;
    add({
      url,
      title: typeof f.title === 'function' ? f.title(p) : f.title,
      sub: f.num(p),
      note: 'From their application',
    });
  });

  (p.documents || []).forEach((url, i) => add({
    url,
    title: name(url) || `Document ${i + 1}`,
    sub: '',
    note: 'From their application',
  }));

  return out;
}

export const fileNameFrom = name;
